import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Grid,
    Button,
    List,
    ListItem,
    IconButton,
    TextField,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from './../../utils/IconHandler';
import { unitOptions, referenceTypes } from '../../utils/Enums';

const ProductItemManager = forwardRef(({ items = [], setItems, data = [], mode = "reference" }, ref) => {
    const { t } = useTranslation();
    const [formErrors, setFormErrors] = useState({});
    const [showAlert, setShowAlert] = useState(false);

    useImperativeHandle(ref, () => ({
        validateItems
    }));

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => {
                setShowAlert(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    // Filtra las referencias según el modo
    const filteredData = data.filter(ref =>
        (mode === "reference" && [referenceTypes.INSUMO, referenceTypes.PREPARADO, referenceTypes.PRODUCTS].includes(ref.referenceType)) ||
        (mode === "category" && ref.referenceType === referenceTypes.CATEGORIES)
    );

    // Si es categoría, eliminar duplicados (quedarse solo con categorías únicas)
    const filteredCategories = filteredData.reduce((acc, curr) => {
        if (!acc.some(cat => cat.referenceId1 === curr.referenceId1)) {
            acc.push(curr);
        }
        return acc;
    }, []);

    const handleAddItem = () => {
        const newItem = {
            referenceId1: '',
            referenceId2: '',
            referenceType: '',
            quantity: '',
            unit: ''
        };
        setItems([...items, newItem]);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = items.map((item, i) => {
            if (i !== index) return item;
    
            const updatedItem = { ...item, [field]: value };
    
            // Si se selecciona una categoría, asignamos `referenceType: CATEGORIES`
            if (mode === "category" && field === "referenceId1") {
                updatedItem.referenceType = referenceTypes.CATEGORIES;
            }
    
            return updatedItem;
        });
    
        setItems(updatedItems);
    };

    const handleReferenceChange = (index, newValue) => {
        const updatedItems = items.map((item, i) => {
            if (i !== index) return item;

            const suggestedUnit = newValue?.referenceType === referenceTypes.INSUMO
                ? newValue?.unit || ''
                : item.unit;

            return {
                ...item,
                referenceId1: newValue?.referenceId1 || '',
                referenceId2: newValue?.referenceType === referenceTypes.CATEGORIES ? newValue?.referenceId2 || '' : '',
                referenceType: newValue?.referenceType || '',
                unit: newValue ? suggestedUnit : ''
            };
        });
        setItems(updatedItems);
    };

    const handleRemoveItem = (index) => {
        const filteredItems = items.filter((_, i) => i !== index);
        setItems(filteredItems);
    };

    const validateItems = () => {
        let errors = {};
        let isValid = true;
    
        items.forEach((item, index) => {
            if (!item.referenceId1) {
                errors[`reference_${index}`] = t('reference_required');
                isValid = false;
            }
    
            if (mode === "category" && !item.referenceId2) {
                errors[`subCategory_${index}`] = t('subcategory_required');
                isValid = false;
            }
    
            if (!item.quantity || item.quantity <= 0) {
                errors[`quantity_${index}`] = t('quantity_required');
                isValid = false;
            }
            if (!item.unit) {
                errors[`unit_${index}`] = t('unit_required');
                isValid = false;
            }
        });
    
        setFormErrors(errors);
        return isValid;
    };    

    const getReferenceLabel = (reference) => {
        const typeLabels = {
            [referenceTypes.INSUMO]: t('supply'), // Insumo
            [referenceTypes.PREPARADO]: t('preparation'), // Preparado
            [referenceTypes.PRODUCTS]: t('product') // Producto
        };

        return `${reference.referenceName1} - ${typeLabels[reference.referenceType] || ''}`;
    };

    return (
        <>
            <Grid container justifyContent="flex-end">
                <Button variant="outlined" startIcon={getIcon('add')} onClick={handleAddItem}>
                    {t('add_item')}
                </Button>
            </Grid>

            {showAlert && <Alert severity="error">{Object.values(formErrors).join('. ')}</Alert>}

            <List>
                {items.map((item, index) => (
                    <ListItem key={index}>
                        <Grid container spacing={2}>
                            {/* Categoría y subcategoría */}
                            {mode === "category" && (
                                <>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth error={!!formErrors[`reference_${index}`]}>
                                            <InputLabel>{t('category')}</InputLabel>
                                            <Select
                                                value={item.referenceId1}
                                                onChange={(e) => handleItemChange(index, 'referenceId1', e.target.value)}
                                                label={t('category')}
                                            >
                                                {filteredCategories.map(category => (
                                                    <MenuItem key={category.referenceId1} value={category.referenceId1}>
                                                        {category.referenceName1}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>{formErrors[`reference_${index}`]}</FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth error={!!formErrors[`subCategory_${index}`]}>
                                            <InputLabel>{t('sub_category')}</InputLabel>
                                            <Select
                                                value={item.referenceId2}
                                                onChange={(e) => handleItemChange(index, 'referenceId2', e.target.value)}
                                                disabled={!item.referenceId1}
                                                label={t('sub_category')}
                                            >
                                                {filteredData
                                                    .filter(cat => cat.referenceId1 === item.referenceId1)
                                                    .map(subCategory => (
                                                        <MenuItem key={subCategory.referenceId2} value={subCategory.referenceId2}>
                                                            {subCategory.referenceName2}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                            <FormHelperText>{formErrors[`subCategory_${index}`]}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}

                            {/* Insumo/Preparación */}
                            {mode === "reference" && (
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={filteredData}
                                        getOptionLabel={(option) => getReferenceLabel(option)}
                                        value={filteredData.find(ref => ref.referenceId1 === item.referenceId1 && ref.referenceType === item.referenceType) || null}
                                        onChange={(event, newValue) => handleReferenceChange(index, newValue)}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label={t('select_reference')} 
                                                fullWidth 
                                                error={!!formErrors[`reference_${index}`]} // Pinta el borde rojo si hay error
                                                helperText={formErrors[`reference_${index}`] || ""} // Muestra el mensaje de error si hay
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            {/* Cantidad */}
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    label={t('quantity')}
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                                    error={!!formErrors[`quantity_${index}`]}
                                    helperText={formErrors[`quantity_${index}`]}
                                    fullWidth
                                />
                            </Grid>

                            {/* Unidad */}
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth error={!!formErrors[`unit_${index}`]}>
                                    <InputLabel>{t('unit')}</InputLabel>
                                    <Select
                                        value={item.unit}
                                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                        label={t('unit')}
                                    >
                                        {unitOptions.map(option => (
                                            <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Eliminar */}
                            <Grid item xs={12} sm={1}>
                                <IconButton onClick={() => handleRemoveItem(index)}>
                                    {getIcon('trash')}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </ListItem>
                ))}
            </List>
        </>
    );
});

export default ProductItemManager;
